/**
 * Terms of Service — full document.
 * Editorial-scientific layout matching Privacy. B2B SaaS terms covering
 * subscription/billing, customer-data ownership, acceptable use, IP, warranties,
 * liability cap, indemnification, termination, dispute resolution.
 */

const SECTIONS = [
  { id: 'definitions',    label: 'Definitions' },
  { id: 'acceptance',     label: 'Acceptance & contract formation' },
  { id: 'service',        label: 'The service we provide' },
  { id: 'subscription',   label: 'Subscription, fees & billing' },
  { id: 'trial',          label: 'Free pilots & trial accounts' },
  { id: 'customer-data',  label: 'Customer data: ownership & licence' },
  { id: 'acceptable-use', label: 'Acceptable use' },
  { id: 'ip',             label: 'Intellectual property' },
  { id: 'confidential',   label: 'Confidentiality' },
  { id: 'security-sla',   label: 'Security, availability & SLA' },
  { id: 'support',        label: 'Support & professional services' },
  { id: 'warranty',       label: 'Warranties & disclaimers' },
  { id: 'liability',      label: 'Limitation of liability' },
  { id: 'indemnity',      label: 'Indemnification' },
  { id: 'termination',    label: 'Term, suspension & termination' },
  { id: 'export',         label: 'Export controls & sanctions' },
  { id: 'force-majeure',  label: 'Force majeure' },
  { id: 'governing-law',  label: 'Governing law & disputes' },
  { id: 'general',        label: 'General provisions' },
  { id: 'contact',        label: 'Contact' },
];

export default function Terms() {
  return (
    <article>
      <section className="cz-legal-hero">
        <div className="cz-wrap">
          <div>
            <span className="cz-mono-label">Document · Master Subscription Agreement</span>
            <h1>Terms of <em>service.</em></h1>
          </div>
          <dl className="cz-legal-hero-meta">
            <dt>Effective</dt><dd>2026-04-01</dd>
            <dt>Version</dt><dd>v 4.0</dd>
            <dt>Supersedes</dt><dd>2025-11-01 (v 3.4)</dd>
            <dt>Reading time</dt><dd>≈ 18 minutes</dd>
          </dl>
        </div>
      </section>

      <section className="cz-legal-body">
        <div className="cz-wrap">
          <div className="cz-legal-grid">
            <nav className="cz-legal-toc" aria-label="Contents">
              <span className="cz-legal-toc-head">§ Contents</span>
              <ol>
                {SECTIONS.map(s => (
                  <li key={s.id}><a href={`#${s.id}`}>{s.label}</a></li>
                ))}
              </ol>
            </nav>

            <div className="cz-legal-article">
              <section>
                <p>
                  These Terms of Service (the &ldquo;<strong>Terms</strong>&rdquo;) form a binding
                  agreement between <strong>CalibZa, Inc.</strong>, a Delaware corporation
                  with offices at 580 California Street, San Francisco, CA 94104
                  (&ldquo;<strong>CalibZa</strong>,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) and the entity that
                  signs an Order Form, accepts these Terms electronically, or otherwise
                  uses the Service (the &ldquo;<strong>Customer</strong>,&rdquo; &ldquo;you&rdquo;).
                </p>
                <div className="cz-legal-callout">
                  <p>
                    By creating an account, accepting an Order Form, or using the
                    Service, you accept these Terms <em>in full.</em> If you don&rsquo;t
                    accept them, do not use the Service.
                  </p>
                </div>
                <p>
                  Where Customer signs a separate written Master Subscription Agreement
                  with CalibZa, that signed agreement controls and supersedes these
                  online Terms to the extent of conflict.
                </p>
              </section>

              <section id="definitions">
                <h2><span className="num">01</span>Definitions</h2>
                <ul>
                  <li><strong>&ldquo;Service&rdquo;</strong> &mdash; the CalibZa hosted software platform, including all dashboards, APIs, integrations, mobile clients, and documentation.</li>
                  <li><strong>&ldquo;Customer Data&rdquo;</strong> &mdash; all data Customer or its Authorised Users submit to the Service, including employee records, performance data, feedback, and uploaded files.</li>
                  <li><strong>&ldquo;Authorised User&rdquo;</strong> &mdash; an employee, contractor, or agent of Customer for whom Customer has provisioned an account.</li>
                  <li><strong>&ldquo;Order Form&rdquo;</strong> &mdash; a written or electronic ordering document executed between the parties referencing these Terms.</li>
                  <li><strong>&ldquo;Subscription Term&rdquo;</strong> &mdash; the period stated on the Order Form during which Customer is licensed to use the Service.</li>
                  <li><strong>&ldquo;DPA&rdquo;</strong> &mdash; the Data Processing Addendum incorporated by reference, governing CalibZa&rsquo;s processing of personal data on Customer&rsquo;s behalf.</li>
                  <li><strong>&ldquo;Documentation&rdquo;</strong> &mdash; the technical and end-user materials published at <code>docs.calibza.com</code>.</li>
                  <li><strong>&ldquo;Confidential Information&rdquo;</strong> &mdash; non-public information disclosed by either party that is marked confidential or that a reasonable person would understand to be confidential.</li>
                </ul>
              </section>

              <section id="acceptance">
                <h2><span className="num">02</span>Acceptance &amp; contract formation</h2>
                <p>
                  These Terms come into force at the earliest of (a) the Effective Date
                  on a signed Order Form, (b) the date you click &ldquo;Open workspace&rdquo;
                  on our website, or (c) the date you first access the Service. The
                  individual accepting these Terms represents that they have authority
                  to bind Customer.
                </p>
                <p>
                  We may modify these Terms by giving 30 days&rsquo; notice to administrators
                  via email and an in-app banner. Material changes that diminish your
                  rights will not apply to a current Subscription Term unless required
                  by law.
                </p>
              </section>

              <section id="service">
                <h2><span className="num">03</span>The service we provide</h2>
                <p>
                  Subject to these Terms and timely payment of fees, CalibZa grants
                  Customer a worldwide, non-exclusive, non-transferable, non-sublicensable
                  right to access and use the Service for its internal business operations
                  during the Subscription Term, in accordance with the Documentation
                  and any limits on the Order Form (e.g. seat counts, modules enabled).
                </p>
                <p>
                  We continually improve the Service. We may modify features, add new
                  ones, or remove deprecated ones. We will not materially diminish the
                  core functionality during a paid Subscription Term without giving you
                  the option to terminate and receive a pro-rata refund.
                </p>
              </section>

              <section id="subscription">
                <h2><span className="num">04</span>Subscription, fees &amp; billing</h2>

                <h3>Pricing</h3>
                <p>
                  Fees are stated on the Order Form or, for self-serve plans, on
                  <a href="/pricing"> calibza.com/pricing</a>. Fees are exclusive of
                  taxes, withholdings, and bank charges, all of which are Customer&rsquo;s
                  responsibility (other than CalibZa&rsquo;s income tax).
                </p>

                <h3>Billing cycle</h3>
                <p>
                  Self-serve plans are billed monthly or annually in advance. Enterprise
                  Order Forms are invoiced on the cadence stated therein, payable
                  net-30 from invoice date. Late amounts accrue interest at 1.5% per
                  month or the maximum rate permitted by law, whichever is less.
                </p>

                <h3>Seat true-ups</h3>
                <p>
                  If average seat usage during a billing period exceeds the licensed
                  seat count, the overage is billed at the then-current per-seat rate
                  on the next invoice. We will notify administrators when usage approaches
                  the licensed cap.
                </p>

                <h3>Renewals</h3>
                <p>
                  Annual subscriptions auto-renew for successive 12-month terms unless
                  either party gives written notice of non-renewal at least 30 days
                  before the current term ends. Renewal pricing may increase by no more
                  than 7% over the prior term&rsquo;s effective per-seat rate, or CPI,
                  whichever is greater.
                </p>

                <h3>Refunds</h3>
                <p>
                  Fees are non-refundable except as expressly stated in these Terms or
                  required by law. Where Customer terminates for our uncured material
                  breach (see &sect;15), we refund pre-paid fees attributable to the
                  unused remainder of the Subscription Term.
                </p>
              </section>

              <section id="trial">
                <h2><span className="num">05</span>Free pilots &amp; trial accounts</h2>
                <p>
                  We offer a 90-day free pilot for organisations with up to 20 Authorised
                  Users. Pilot accounts have access to all current modules but exclude
                  uptime SLA credits and dedicated CSM coverage. Pilot data is retained
                  for 30 days after expiry and then permanently deleted unless the
                  account converts to paid.
                </p>
                <p>
                  THE PILOT SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND WITHOUT THE WARRANTIES
                  IN &sect;12.
                </p>
              </section>

              <section id="customer-data">
                <h2><span className="num">06</span>Customer data: ownership &amp; licence</h2>
                <p>
                  As between the parties, Customer owns all right, title, and interest
                  in Customer Data. CalibZa claims no ownership over Customer Data and
                  will not access it except (a) to provide and improve the Service, (b)
                  for support requests authorised by Customer, (c) to comply with law,
                  or (d) to protect the security and integrity of the Service.
                </p>
                <p>
                  Customer grants CalibZa a worldwide, royalty-free licence to host,
                  process, copy, transmit, and display Customer Data solely as needed to
                  provide the Service.
                </p>
                <p>
                  CalibZa may collect and use <strong>Aggregated, De-identified Data</strong>
                  &mdash; data derived from Customer Data that has been irreversibly stripped
                  of identifiers and aggregated with similar data from other customers
                  &mdash; to operate, analyse, improve, and benchmark the Service. Aggregated
                  Data does not constitute Customer Data.
                </p>
                <p>
                  Customer may export its Customer Data at any time during the
                  Subscription Term and for 30 days after termination, in JSON or CSV
                  via the Service&rsquo;s export tools or REST API.
                </p>
              </section>

              <section id="acceptable-use">
                <h2><span className="num">07</span>Acceptable use</h2>
                <p>Customer and its Authorised Users will not:</p>
                <ul>
                  <li>Reverse-engineer, decompile, or attempt to derive the source code of the Service, except to the extent permitted by mandatory law;</li>
                  <li>Resell, sublicense, time-share, or rent the Service to a third party;</li>
                  <li>Use the Service to build a competing product;</li>
                  <li>Submit any content that is unlawful, defamatory, infringing, or that contains malware;</li>
                  <li>Probe, scan, or test the vulnerability of the Service except under our published disclosure programme;</li>
                  <li>Bypass or attempt to bypass any rate limits, authentication, or audit-logging mechanisms;</li>
                  <li>Use the Service to make automated decisions about employees that produce legal or similarly significant effects without meaningful human review (per GDPR Art. 22);</li>
                  <li>Violate any applicable law, including employment, anti-discrimination, privacy, or export-control laws.</li>
                </ul>
                <p>
                  We may suspend access in whole or in part if we reasonably believe
                  these restrictions are being violated and the violation poses an
                  imminent risk to the Service or other customers.
                </p>
              </section>

              <section id="ip">
                <h2><span className="num">08</span>Intellectual property</h2>
                <p>
                  CalibZa retains all right, title, and interest in and to the Service,
                  the underlying software, the calibration algorithms, the scoring
                  models, the user interface, the Documentation, and any improvements
                  thereto. No rights are granted by implication, estoppel, or otherwise
                  except those expressly set forth in these Terms.
                </p>
                <p>
                  Feedback that Customer or any Authorised User provides about the
                  Service may be used by CalibZa for any purpose, on a perpetual,
                  worldwide, royalty-free basis, without obligation to attribute or
                  compensate. Customer warrants such feedback is not Confidential
                  Information of Customer.
                </p>
              </section>

              <section id="confidential">
                <h2><span className="num">09</span>Confidentiality</h2>
                <p>
                  Each party will (a) protect the other&rsquo;s Confidential Information using
                  the same care it uses for its own confidential information of like
                  importance, but no less than reasonable care, (b) use Confidential
                  Information only as necessary to perform under these Terms, and (c)
                  disclose Confidential Information only to employees, contractors,
                  and advisors with a need to know who are bound by similar confidentiality
                  obligations.
                </p>
                <p>
                  Confidentiality obligations survive termination for three years,
                  except for trade secrets which remain confidential indefinitely.
                  Disclosure compelled by law is permitted provided the disclosing
                  party gives prompt notice and reasonable cooperation in seeking a
                  protective order.
                </p>
              </section>

              <section id="security-sla">
                <h2><span className="num">10</span>Security, availability &amp; SLA</h2>
                <p>
                  CalibZa maintains an information security programme aligned with
                  SOC 2 Type II controls and (in progress) ISO 27001. Details are in
                  our Trust Centre at <code>trust.calibza.com</code> and the DPA.
                </p>
                <p>
                  We target <strong>99.9% monthly uptime</strong> for paid Subscriptions,
                  excluding scheduled maintenance (announced ≥ 72 hours in advance) and
                  Force Majeure events. Customers experiencing below-target uptime
                  receive service credits per the schedule below, capped at 30% of the
                  monthly fee for the affected period:
                </p>
                <table className="cz-legal-table">
                  <thead><tr><th>Monthly uptime</th><th>Credit</th></tr></thead>
                  <tbody>
                    <tr><td>&lt; 99.9% &amp; ≥ 99.0%</td><td>10%</td></tr>
                    <tr><td>&lt; 99.0% &amp; ≥ 95.0%</td><td>20%</td></tr>
                    <tr><td>&lt; 95.0%</td><td>30%</td></tr>
                  </tbody>
                </table>
                <p>
                  Credits are Customer&rsquo;s sole and exclusive remedy for SLA breaches
                  and must be claimed in writing within 30 days of the affected period.
                </p>
              </section>

              <section id="support">
                <h2><span className="num">11</span>Support &amp; professional services</h2>
                <p>
                  Standard support is included with every paid Subscription: 24&times;5
                  email support with 8-business-hour response for non-urgent issues
                  and 1-hour response for confirmed P1 incidents (Service unavailable).
                  Enterprise plans include 24&times;7 phone support and a named Customer
                  Success Manager.
                </p>
                <p>
                  Professional services (data migration, custom integration, training
                  workshops) are scoped under separate Statements of Work referencing
                  these Terms. Hourly rates and minimum engagements are stated on each
                  SOW.
                </p>
              </section>

              <section id="warranty">
                <h2><span className="num">12</span>Warranties &amp; disclaimers</h2>
                <p>
                  CalibZa warrants that the Service will perform materially in accordance
                  with the Documentation during the Subscription Term. Customer&rsquo;s
                  exclusive remedy for breach of this warranty is, at CalibZa&rsquo;s option,
                  (a) re-performance of the non-conforming Service, or (b) refund of
                  fees paid for the non-conforming portion.
                </p>
                <div className="cz-legal-callout">
                  <p>
                    EXCEPT AS EXPRESSLY STATED, THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo;
                    AND &ldquo;AS AVAILABLE.&rdquo; CALIBZA <em>DISCLAIMS</em> ALL OTHER
                    WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY,
                    FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
                    NO ADVICE OR INFORMATION OBTAINED FROM THE SERVICE CREATES ANY
                    WARRANTY NOT EXPRESSLY STATED HEREIN.
                  </p>
                </div>
              </section>

              <section id="liability">
                <h2><span className="num">13</span>Limitation of liability</h2>
                <p>
                  EXCEPT FOR (i) BREACHES OF CONFIDENTIALITY, (ii) INDEMNIFICATION
                  OBLIGATIONS, (iii) WILFUL MISCONDUCT, OR (iv) AMOUNTS OWED TO
                  CALIBZA, NEITHER PARTY&rsquo;S AGGREGATE LIABILITY UNDER OR IN CONNECTION
                  WITH THESE TERMS WILL EXCEED <strong>THE FEES PAID BY CUSTOMER TO
                  CALIBZA IN THE 12 MONTHS PRECEDING THE EVENT</strong> GIVING RISE TO
                  THE CLAIM.
                </p>
                <p>
                  IN NO EVENT WILL EITHER PARTY BE LIABLE FOR INDIRECT, INCIDENTAL,
                  SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS,
                  REVENUES, GOODWILL, OR DATA, EVEN IF ADVISED OF THE POSSIBILITY.
                  THESE LIMITATIONS APPLY REGARDLESS OF THE THEORY OF LIABILITY
                  (CONTRACT, TORT, STATUTE, OR OTHERWISE).
                </p>
              </section>

              <section id="indemnity">
                <h2><span className="num">14</span>Indemnification</h2>

                <h3>By CalibZa</h3>
                <p>
                  CalibZa will defend, indemnify, and hold harmless Customer against any
                  third-party claim alleging that the Service, as provided by CalibZa
                  and used in accordance with these Terms, infringes the third party&rsquo;s
                  patent, copyright, trade secret, or trademark, and will pay damages
                  finally awarded or settlement amounts agreed by CalibZa.
                </p>
                <p>
                  If the Service is, or in CalibZa&rsquo;s reasonable opinion is likely to
                  become, the subject of an infringement claim, CalibZa may at its
                  option (a) procure a licence, (b) modify the Service to be non-infringing,
                  or (c) terminate the affected Subscription and refund pre-paid fees
                  attributable to the unused term.
                </p>

                <h3>By Customer</h3>
                <p>
                  Customer will defend, indemnify, and hold harmless CalibZa against any
                  third-party claim arising from (a) Customer Data, (b) Customer&rsquo;s
                  use of the Service in violation of these Terms or applicable law,
                  including employment and anti-discrimination law, or (c) any
                  employment-related claim by an Authorised User against CalibZa.
                </p>

                <h3>Process</h3>
                <p>
                  The indemnified party must (a) give prompt written notice, (b) grant
                  the indemnifying party sole control of defence and settlement
                  (provided no settlement imposing liability or admission of fault on
                  the indemnified party may be entered without consent, not unreasonably
                  withheld), and (c) provide reasonable cooperation at the indemnifying
                  party&rsquo;s expense.
                </p>
              </section>

              <section id="termination">
                <h2><span className="num">15</span>Term, suspension &amp; termination</h2>
                <p>
                  These Terms remain in force during all active Subscription Terms.
                  Either party may terminate for the other&rsquo;s material breach if the
                  breach is not cured within 30 days of written notice. Customer may
                  terminate at any time without cause, but pre-paid fees are non-refundable
                  except as stated in &sect;04.
                </p>
                <p>
                  CalibZa may suspend access (a) for non-payment continuing more than
                  10 days after written notice, (b) where required by law, or (c) where
                  Authorised User behaviour poses a security risk to the Service. We
                  will restore access promptly upon resolution.
                </p>
                <p>
                  On termination: (a) Customer&rsquo;s rights to access the Service end,
                  (b) all outstanding fees become immediately due, (c) Customer has 30
                  days to export Customer Data, after which CalibZa will delete it
                  except for legal-hold or aggregated/de-identified copies. Sections
                  06, 09, 12, 13, 14, 18, 19, and 20 survive.
                </p>
              </section>

              <section id="export">
                <h2><span className="num">16</span>Export controls &amp; sanctions</h2>
                <p>
                  Customer represents that neither it nor any Authorised User is (a)
                  located in a country subject to US embargo (currently Cuba, Iran,
                  North Korea, Syria, Crimea, Donetsk, and Luhansk), or (b) listed
                  on any US Government list of prohibited or restricted parties.
                  Customer will not use the Service in violation of any export-control
                  or sanctions law.
                </p>
              </section>

              <section id="force-majeure">
                <h2><span className="num">17</span>Force majeure</h2>
                <p>
                  Neither party is liable for delays or failures caused by events beyond
                  reasonable control: natural disasters, war, terrorism, pandemic,
                  governmental action, internet or utility failures, or third-party
                  cloud-provider outages. The affected party will use commercially
                  reasonable efforts to mitigate.
                </p>
              </section>

              <section id="governing-law">
                <h2><span className="num">18</span>Governing law &amp; disputes</h2>
                <p>
                  These Terms are governed by the laws of the State of California,
                  without regard to conflict-of-law rules. The UN Convention on
                  Contracts for the International Sale of Goods does not apply.
                </p>
                <p>
                  Any dispute arising out of or relating to these Terms will be resolved
                  by binding arbitration administered by JAMS in San Francisco, California,
                  before a single arbitrator, in English. Either party may seek injunctive
                  or equitable relief in a court of competent jurisdiction to protect
                  intellectual property or confidentiality. The parties waive any right
                  to a class action.
                </p>
              </section>

              <section id="general">
                <h2><span className="num">19</span>General provisions</h2>
                <ul>
                  <li><strong>Entire agreement.</strong> These Terms, the DPA, any Order Form, and any SOW form the entire agreement and supersede all prior agreements on the subject matter.</li>
                  <li><strong>Order of precedence.</strong> Order Form &gt; DPA &gt; these Terms &gt; Documentation.</li>
                  <li><strong>Assignment.</strong> Neither party may assign without consent, except in connection with a merger, acquisition, or sale of substantially all assets.</li>
                  <li><strong>Notices.</strong> To CalibZa: <code>legal@calibza.com</code> with a hard copy to the address in &sect;20. To Customer: the email of the administrator on file.</li>
                  <li><strong>Independent contractors.</strong> The parties are independent contractors. No partnership or agency.</li>
                  <li><strong>Severability.</strong> If any provision is unenforceable, the remainder remains in force; the unenforceable provision is reformed to the minimum extent necessary.</li>
                  <li><strong>Waiver.</strong> No failure to enforce constitutes a waiver of future rights.</li>
                  <li><strong>Anti-corruption.</strong> Each party complies with the US Foreign Corrupt Practices Act, UK Bribery Act, and analogous laws.</li>
                </ul>
              </section>

              <section id="contact">
                <h2><span className="num">20</span>Contact</h2>
                <p>
                  CalibZa, Inc.<br/>
                  Attn: Legal Department<br/>
                  580 California Street, 12th Floor<br/>
                  San Francisco, CA 94104, USA<br/>
                  Email: <a href="mailto:legal@calibza.com">legal@calibza.com</a><br/>
                  General: <a href="mailto:hello@calibza.com">hello@calibza.com</a>
                </p>
                <p>
                  For privacy matters see our <a href="/privacy">Privacy Policy</a>.
                  For the Data Processing Addendum, request a countersigned copy from
                  <a href="mailto:dpa@calibza.com"> dpa@calibza.com</a>.
                </p>
              </section>

              <div className="cz-legal-footer">
                <span>End of document · <strong>Master Subscription Agreement v 4.0</strong></span>
                <span>Set in Fraunces · <strong>© CalibZa, Inc.</strong></span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
